// File: components/pages/HomePage.tsx
"use client";

import HardNavLink from "@/components/shared/HardNavLink";
import { useState, useEffect } from "react";
import {
  Flame, Target, Pin, ChevronRight,
  Play, Copy, Sparkles, CheckCircle2,
  Gift, ArrowRight, Clock, Star, Lock,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import QuickQuiz, { QuickQuizSkeleton } from "@/components/dashboard/QuickQuiz";
import HeroBanner from "@/components/dashboard/HeroBanner";
import { fetchRandomQuestion } from "@/actions/quiz";
import type { PinnedPath, ResumeItem, DailyProgress } from "@/actions/homepage";
import type { Question } from "@/actions/quiz";
import type { Quote, Stats24h } from "@/actions/stats";

// ── Featured item type ──
interface FeaturedItem {
  emoji: string;
  title: string;
  meta: string;
  href: string;
}

// ── QuizSection: client-side fetch giữ nguyên ───────────
function QuizSection() {
  const [question, setQuestion] = useState<Question | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchRandomQuestion()
      .then((q) => { if (q) setQuestion(q); else setError(true); })
      .catch(() => setError(true));
  }, []);

  if (error) return (
    <div className="bg-white border border-border rounded-xl shadow-card p-6 text-center">
      <p className="text-[13px] text-muted-foreground mb-2">Không tải được câu hỏi</p>
      <button onClick={() => { setError(false); fetchRandomQuestion().then(q => { if (q) setQuestion(q); else setError(true); }).catch(() => setError(true)); }}
        className="text-[12px] text-teal-600 font-bold hover:underline">Thử lại</button>
    </div>
  );

  if (!question) return <QuickQuizSkeleton />;
  return <QuickQuiz initialQuestion={question} />;
}

// ── Props from Server Component ──
interface HomePageProps {
  pinnedPaths: PinnedPath[];
  resumeItems: ResumeItem[];
  dailyProgress: DailyProgress;
  quote: Quote | null;
  stats: Stats24h;
  featured: { paths: any[]; exams: any[] };
  hideHero?: boolean;
}

// ── Page ──────────────────────────────────────────────────────
export default function HomePage({
  pinnedPaths,
  resumeItems,
  dailyProgress,
  quote,
  stats,
  featured,
hideHero,
}: HomePageProps) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  // ─── Local counter cho thanh "Tiến độ hôm nay" — +1 mỗi câu answer ───
  // Không đụng DB, data thật user xem tại /streak
  const [localDone, setLocalDone] = useState(dailyProgress.todayDone);
  const [localStreak, setLocalStreak] = useState(dailyProgress.streakDays);

  useEffect(() => {
    const onAnswered = (e: Event) => {
      const detail = (e as CustomEvent<{ count?: number }>).detail ?? {};
      const inc = detail.count ?? 1;
      setLocalDone(prev => {
        const next = prev + inc;
        // Khi vừa chạm threshold & hôm nay chưa có streak → UI +1 streak
        if (prev < dailyProgress.todayGoal && next >= dailyProgress.todayGoal) {
          setLocalStreak(s => s + 1);
        }
        return next;
      });
    };
    window.addEventListener("jb:question-answered", onAnswered);
    return () => window.removeEventListener("jb:question-answered", onAnswered);
  }, [dailyProgress.todayGoal]);

  // ── Build featured display items: 4 đề thi xem nhiều nhất ──
  const featuredItems: FeaturedItem[] = [];
  for (const e of (featured.exams ?? []).slice(0, 4)) {
    const qCount = e.total_questions ?? 0;
    const time = e.time_limit ? `${e.time_limit} phút` : "";
    const rating = e.rating ? `${Number(e.rating).toFixed(1)}★` : "";
    const price = (!e.price || e.price === 0) ? "Free" : "";
    const meta = [qCount > 0 ? `${qCount} câu` : null, time, rating, price].filter(Boolean).join(" · ");
    const href = e.slug ? `/exams/${e.slug}` : `/exams/${e.id}`;
    featuredItems.push({ emoji: e.emoji ?? "📝", title: e.title ?? "Đề thi", meta: meta || "Mới", href });
  }

  const FALLBACK_FEATURED: FeaturedItem[] = [
    { emoji: "🏦", title: "Tín dụng VCB 2025", meta: "520 câu · 4.9★", href: "/paths" },
    { emoji: "📝", title: "Mock Test BIDV", meta: "80 câu · 90 phút", href: "/exams" },
    { emoji: "⚡", title: "Mini Test 5C", meta: "20 câu · Free", href: "/exams" },
    { emoji: "🛡️", title: "KSNB Nâng cao", meta: "160 câu · 4.6★", href: "/paths" },
  ];
  const displayFeatured = featuredItems.length > 0 ? featuredItems : FALLBACK_FEATURED;

  function handleCopyLink() {
    const code = user?.referral_code ?? user?.id ?? "jbai";
    navigator.clipboard.writeText(`${window.location.origin}/ref/${code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="max-w-[62rem] mx-auto px-6 py-6 max-md:px-3.5">

      {/* ══ HERO BANNER + 24h STATS ══ */}
      {!hideHero && <HeroBanner user={user ?? null} quote={quote} stats={stats} />}

      {/* ══ 2. MỤC TIÊU ĐANG THEO DÕI ══ */}
      <div className="relative bg-white border border-border rounded-xl shadow-card overflow-hidden mb-5">
        {!user && (
          <div className="absolute inset-0 backdrop-blur-[2px] bg-background/50 flex flex-col items-center justify-center z-10 rounded-xl">
            <Lock className="w-6 h-6 text-muted-foreground mb-2" />
            <p className="text-[12px] font-bold text-muted-foreground">Đăng nhập để xem tiến độ</p>
          </div>
        )}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Pin className="w-4 h-4 text-amber-500 fill-amber-400" />
            <span className="text-[13px] font-bold text-foreground">Mục tiêu đang theo dõi</span>
          </div>
          <HardNavLink href="/my-paths" className="text-[11px] text-teal-600 font-bold hover:underline flex items-center gap-1">
            Xem tất cả <ArrowRight className="w-3 h-3" />
          </HardNavLink>
        </div>

        {/* Empty state */}
        {pinnedPaths.length === 0 && user && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Pin className="w-6 h-6 text-muted-foreground/20 mb-2" />
            <p className="text-[12px] text-muted-foreground mb-1">Chưa có lộ trình nào</p>
            <HardNavLink href="/explore" className="text-[11px] text-teal-600 font-semibold hover:underline">
              Khám phá lộ trình →
            </HardNavLink>
          </div>
        )}

        {/* Real data */}
        {pinnedPaths.length > 0 && (
          <div className="divide-y divide-border">
            {pinnedPaths.map(path => (
              <HardNavLink key={path.id} href={`/paths/${path.id}`} className="flex items-center gap-3.5 px-5 py-3 hover:bg-muted/30 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-xl shrink-0">{path.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-foreground truncate mb-1.5">{path.title}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${path.progress >= 100 ? "bg-emerald-500" : "bg-gradient-to-r from-teal-600 to-teal-400"}`} style={{ width: `${path.progress}%` }} />
                    </div>
                    <span className={`text-[10px] font-bold shrink-0 ${path.progress >= 100 ? "text-emerald-600" : "text-orange-500"}`}>{path.progress}%</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{path.remaining}</div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
              </HardNavLink>
            ))}
          </div>
        )}
      </div>

      {/* ══ TIẾN ĐỘ HÔM NAY (đặt sát trước Quiz để user thấy thanh tăng) ══ */}
      <HardNavLink href="/streak" className="block">
        <div className="bg-white border border-border rounded-xl shadow-card p-4 mb-5 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[13px] font-bold text-foreground">Tiến độ hôm nay</span>
              <span className="text-[12px] font-extrabold text-orange-500 tabular-nums">
                {Math.min(localDone, dailyProgress.todayGoal)} / {dailyProgress.todayGoal} câu
              </span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, (localDone / dailyProgress.todayGoal) * 100)}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="text-[12px] font-bold text-orange-600 tabular-nums">{localStreak} ngày 🔥</span>
          </div>
        </div>
      </HardNavLink>

      {/* ══ 3. NHIỆM VỤ HÔM NAY ══ */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-orange-500" />
          <h2 className="text-[14px] font-extrabold text-teal-800">Nhiệm vụ hôm nay</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <QuizSection />
          </div>

          {user && (
            <div className="flex flex-col gap-3">
              {/* Card Mời bạn bè — outer link + nested copy button */}
              <div className="relative bg-gradient-to-br from-[#7c3aed] to-[#a855f7] rounded-xl p-4 text-white flex-1 hover:shadow-[0_4px_20px_rgba(124,58,237,.4)] transition-all">
                <HardNavLink href="/invite" aria-label="Mời bạn bè" className="absolute inset-0 rounded-xl z-0" />
                <div className="relative z-10 pointer-events-none">
                  <Gift className="w-5 h-5 text-white/70 mb-2" />
                  <div className="text-[12px] font-bold mb-1">Mời bạn bè</div>
                  <p className="text-[10px] text-white/60 leading-relaxed mb-3">Mời 1 bạn đăng ký = Nhận 1 💎</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCopyLink(); }}
                  className="relative z-10 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/20 text-white text-[11px] font-bold hover:bg-white/30 transition-colors"
                >
                  {copied ? <><CheckCircle2 className="w-3 h-3" />Đã copy!</> : <><Copy className="w-3 h-3" />Copy link mời</>}
                </button>
              </div>

              {/* Card Sáng tạo nội dung */}
              <HardNavLink href="/studio" className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-4 text-white flex-1 block">
                <Sparkles className="w-5 h-5 text-white/70 mb-2" />
                <div className="text-[12px] font-bold mb-1">Sáng tạo nội dung</div>
                <p className="text-[10px] text-white/60 leading-relaxed mb-3">Tạo lộ trình/đề thi đầu tiên</p>
                <span className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/20 text-white text-[11px] font-bold hover:bg-white/30 transition-colors">
                  <ArrowRight className="w-3 h-3" />Creator Studio
                </span>
              </HardNavLink>
            </div>
          )}
        </div>
      </div>

      {/* ══ 4. TIẾP TỤC HỌC ══ */}
      <div className="relative bg-white border border-border rounded-xl shadow-card overflow-hidden mb-5">
        {!user && (
          <div className="absolute inset-0 backdrop-blur-[2px] bg-background/50 flex flex-col items-center justify-center z-10 rounded-xl">
            <Lock className="w-6 h-6 text-muted-foreground mb-2" />
            <p className="text-[12px] font-bold text-muted-foreground">Đăng nhập để tiếp tục</p>
          </div>
        )}
        <div className="flex items-center px-5 py-3.5 border-b border-border gap-2">
          <Clock className="w-4 h-4 text-teal-600" />
          <span className="text-[13px] font-bold text-foreground">Tiếp tục từ lần trước</span>
        </div>

        {/* Empty state */}
        {resumeItems.length === 0 && user && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="w-6 h-6 text-muted-foreground/20 mb-2" />
            <p className="text-[12px] text-muted-foreground mb-1">Chưa có hoạt động nào</p>
            <HardNavLink href="/explore" className="text-[11px] text-teal-600 font-semibold hover:underline">
              Bắt đầu học ngay →
            </HardNavLink>
          </div>
        )}

        {/* Real data */}
        {resumeItems.length > 0 && (
          <div className="divide-y divide-border">
            {resumeItems.map(item => {
              const href = item.type === "path" ? `/paths/${item.id}` : `/exams/${item.id}`;
              return (
                <HardNavLink key={item.id} href={href} className="flex items-center gap-3.5 px-5 py-3.5 hover:bg-muted/30 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center text-xl shrink-0">{item.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-foreground truncate">{item.title}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {item.tags.map(t => <span key={t} className="text-[9px] px-1.5 py-[1px] rounded-full bg-teal-50 text-teal-700 border border-teal-200 font-bold">{t}</span>)}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">Lần cuối: {item.lastAt}</div>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-teal-800 text-white text-[11px] font-bold hover:bg-teal-700 transition-colors">
                    <Play className="w-3 h-3" />Tiếp tục
                  </span>
                </HardNavLink>
              );
            })}
          </div>
        )}
      </div>

      {/* ══ 5. KHÁM PHÁ NỔI BẬT ══ */}
      <div className="bg-white border border-border rounded-xl shadow-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="text-[13px] font-bold text-foreground">Nổi bật tuần này</span>
          </div>
          <HardNavLink href="/exams" className="text-[11px] text-teal-600 font-bold hover:underline">Xem thêm</HardNavLink>
        </div>
        <div className="p-4 grid grid-cols-2 gap-2.5">
          {displayFeatured.map((item, i) => (
            <HardNavLink key={i} href={item.href} className="flex items-center gap-2.5 p-3 rounded-xl border border-border hover:border-teal-400/50 hover:bg-teal-50/30 transition-all cursor-pointer group">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center text-lg shrink-0">{item.emoji}</div>
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-foreground truncate group-hover:text-teal-700">{item.title}</div>
                <div className="text-[10px] text-muted-foreground">{item.meta}</div>
              </div>
            </HardNavLink>
          ))}
        </div>
      </div>

      <div className="h-6" />
    </div>
  );
}